/**
 * Collection Recommendations
 * Shows personalized recommendations above collection product grid
 * Only displays if visitor has enough interactions (recommendations-ready API)
 */

(function() {
  'use strict';

  const SUPABASE_URL = 'https://bernard-recommender.bernard-legoff-29.workers.dev';

  const elements = {
    container: null,
    loading: null,
    content: null,
    list: null
  };

  let productLimit = 6;
  let initialVisible = 2;

  function init() {
    elements.container = document.getElementById('collection-recommendations');
    if (!elements.container) return;

    elements.loading = elements.container.querySelector('.collection-reco-loading');
    elements.content = elements.container.querySelector('.collection-reco-content');
    elements.list = elements.container.querySelector('.collection-reco-list');

    // Read settings from data attributes
    productLimit = parseInt(elements.container.dataset.limit, 10) || 6;
    initialVisible = parseInt(elements.container.dataset.initialVisible, 10) || 2;

    loadRecommendations();
  }

  function getInitialVisible() {
    // On mobile, use 2 or the configured value, whichever is smaller
    if (window.innerWidth < 768) {
      return Math.min(2, initialVisible);
    }
    return initialVisible;
  }

  async function getVisitorId() {
    if (window._rm && typeof window._rm.gv === 'function') {
      try {
        const visitorId = await window._rm.gv();
        if (visitorId) return visitorId;
      } catch (e) {
        console.warn('[CollectionReco] Failed to get visitor ID:', e);
      }
    }

    await new Promise(resolve => setTimeout(resolve, 1000));

    if (window._rm && typeof window._rm.gv === 'function') {
      try {
        return await window._rm.gv();
      } catch (e) {
        console.warn('[CollectionReco] Retry failed:', e);
      }
    }

    return null;
  }

  async function checkRecommendationsReady(visitorId) {
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/recommendations-ready`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitorId })
      });

      if (!response.ok) return { ready: false };
      return response.json();
    } catch (e) {
      console.warn('[CollectionReco] Ready check failed:', e);
      return { ready: false };
    }
  }

  async function fetchRecommendations(visitorId) {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/recommendations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitorId, limit: productLimit })
    });

    if (!response.ok) throw new Error(`Recommendations API error: ${response.status}`);
    return response.json();
  }

  function showLoading() {
    if (elements.container) elements.container.style.display = 'block';
    if (elements.loading) elements.loading.style.display = 'flex';
    if (elements.content) elements.content.style.display = 'none';
  }

  function hideContainer() {
    if (elements.container) elements.container.style.display = 'none';
  }

  function showContent() {
    if (elements.container) elements.container.style.display = 'block';
    if (elements.loading) elements.loading.style.display = 'none';
    if (elements.content) elements.content.style.display = 'block';
  }

  function setupCollapsible(totalProducts) {
    if (!elements.list) return;

    const visibleCount = getInitialVisible();
    const cards = elements.list.querySelectorAll('product-card');

    if (cards.length <= visibleCount) return;

    // Hide cards beyond initial visible
    cards.forEach((card, index) => {
      if (index >= visibleCount) {
        card.classList.add('collapsed-item');
        card.style.display = 'none';
      }
    });

    // Remove existing wrapper if any
    const existingWrapper = elements.container.querySelector('.collection-reco-show-more-wrapper');
    if (existingWrapper) existingWrapper.remove();

    // Create show more button
    const wrapper = document.createElement('div');
    wrapper.className = 'collection-reco-show-more-wrapper';

    const showMoreBtn = document.createElement('button');
    showMoreBtn.type = 'button';
    showMoreBtn.className = 'button outline collection-reco-show-more-btn';
    showMoreBtn.innerHTML = `Voir plus (${totalProducts - visibleCount})`;
    showMoreBtn.dataset.expanded = 'false';

    showMoreBtn.addEventListener('click', () => {
      const isExpanded = showMoreBtn.dataset.expanded === 'true';

      cards.forEach((card, index) => {
        if (index >= visibleCount) {
          card.style.display = isExpanded ? 'none' : '';
        }
      });

      showMoreBtn.dataset.expanded = isExpanded ? 'false' : 'true';
      showMoreBtn.innerHTML = isExpanded
        ? `Voir plus (${totalProducts - visibleCount})`
        : 'Voir moins';
    });

    wrapper.appendChild(showMoreBtn);
    elements.list.after(wrapper);
  }

  async function loadRecommendations() {
    try {
      const visitorId = await getVisitorId();

      if (!visitorId) {
        console.log('[CollectionReco] No visitor ID');
        return;
      }

      // Check if recommendations are ready first (don't show anything yet)
      const readyCheck = await checkRecommendationsReady(visitorId);

      if (!readyCheck.ready) {
        console.log('[CollectionReco] Not ready:', readyCheck.interactionCount, '/', readyCheck.threshold);
        return;
      }

      // Only show loading after we know visitor has recommendations
      showLoading();
      console.log('[CollectionReco] Ready, fetching recommendations...');

      const data = await fetchRecommendations(visitorId);

      if (!data.products || data.products.length === 0) {
        console.log('[CollectionReco] No products returned');
        hideContainer();
        return;
      }

      // Render products in list layout (no hover images for list)
      if (window.ProductCardDynamic && elements.list) {
        window.ProductCardDynamic.renderGrid(elements.list, data.products, {
          impressionContext: 'collection-recommendations',
          enableHoverImages: false,
          showFeedback: true
        });
      }

      showContent();

      // Setup collapsible after render
      setupCollapsible(data.products.length);

      console.log('[CollectionReco] Rendered', data.products.length, 'products (list view)');

    } catch (error) {
      console.error('[CollectionReco] Error:', error);
      hideContainer();
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
